import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button, Input, Spin, message, Tooltip } from "antd"; // Added Tooltip for night icon
import moment from "moment";
import CryptoJS from "crypto-js";
import { useGlobalData } from "./../../context/GlobalDataContext";
import { PhoneOutlined, UserOutlined, BilibiliFilled as ChildCareOutlined, MoonOutlined } from "@ant-design/icons";
import Select from "react-select";

const ReservationList = () => {
  const [searchText, setSearchText] = useState("");
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(null);
  const { Search } = Input;
  const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
  const token = localStorage.getItem("token");
  const { business_date } = useGlobalData();
  const [selectedDate, setSelectedDate] = useState(moment(business_date));
  const maximizeBtnRef = useRef(null);

  useEffect(() => {
    fetchReservations();
  }, [selectedDate]);

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiURL}/booking/reservations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch reservations.");
      const data = await response.json();
      if (data.success) setReservations(data.reservations);
      else throw new Error("No reservations found.");
    } catch (err) {
      setError(err.message);
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredReservations = reservations.filter((reservation) => {
    const matchesSearch =
      reservation.booking_number.toLowerCase().includes(searchText.toLowerCase()) ||
      reservation.guest_name.toLowerCase().includes(searchText.toLowerCase()) ||
      reservation.guest_email.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = !statusFilter || reservation.booking_status === statusFilter.value;
    const matchesSource = !sourceFilter || reservation.booking_source === sourceFilter.value;
    return matchesSearch && matchesStatus && matchesSource;
  });

  const handleDateChange = (direction) => {
    setSelectedDate((prevDate) =>
      direction === "prev" ? prevDate.subtract(1, "day") : prevDate.add(1, "day")
    );
  };

  const secretKey = "abde100199199111";

  const statusOptions = [
    { value: "reserved", label: "Reserved" },
    { value: "checked_in", label: "Checked In" },
    { value: "checked_out", label: "Checked Out" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const sourceOptions = [
    { value: "Direct", label: "Direct" },
    { value: "Online", label: "Online" },
    { value: "Agency", label: "Agency" },
  ];

  const handleFilterReset = () => {
    setStatusFilter(null);
    setSourceFilter(null);
    setSearchText("");
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-2">
              <h3 className="page-title text-dark fw-bold">Reservations</h3>
            </div>
            <div className="col-md-10 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center me-3">
                <Button icon={<i className="fa fa-chevron-circle-left text-primary" />} onClick={() => handleDateChange("prev")} />
                <span className="mx-2 fw-bold text-dark" style={{ fontSize: "16px" }}>
                  {selectedDate.format("DD MMM YYYY")}
                </span>
                <Button icon={<i className="fa fa-chevron-circle-right text-primary" />} onClick={() => handleDateChange("next")} />
              </div>
              <Search
                placeholder="Search by Name, Email, or Booking ID"
                allowClear
                enterButton="Search"
                size="large"
                onChange={(e) => setSearchText(e.target.value)}
                className="shadow-sm"
                style={{ maxWidth: 400 }}
              />
              <div className="d-flex align-items-center">
                <div className="view-icons me-2">
                  <Link to="/employees" className="btn btn-link text-dark"><i className="fa fa-th" /></Link>
                  <Link to="/employee-list" className="btn btn-link active text-dark mx-1"><i className="fa fa-bars" /></Link>
                  <Link to="#" className="btn btn-link text-dark" data-bs-toggle="modal" data-bs-target="#scrollable-modal">
                    <i className="las la-filter" />
                  </Link>
                  <Link to="#" className="btn btn-link text-dark" id="collapse-header" ref={maximizeBtnRef}>
                    <i className="las la-expand-arrows-alt" />
                  </Link>
                  <Link to="#" className="btn btn-link text-dark" data-bs-toggle="modal" data-bs-target="#export">
                    <i className="las la-file-export" />
                  </Link>
                </div>
                <Link to="/hospitality/guest-reservation" className="btn add-btn custom-btn bg-gradient-primary text-white">
                  <i className="la la-plus-circle custom-icon" /> Add Reservation
                </Link>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center my-5">
            <Spin size="large" />
          </div>
        ) : error ? (
          <p className="text-danger text-center my-5">{error}</p>
        ) : filteredReservations.length === 0 ? (
          <div className="no-data-container text-center mt-4">
            <img
              src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
              alt="No data available"
              style={{ width: "150px", marginBottom: "20px" }}
            />
            <h3 className="text-dark">No Data Available</h3>
            <p className="text-muted">No reservations found for the selected date.</p>
          </div>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mt-3">
            {filteredReservations.map((reservation) => {
              const encryptedId = CryptoJS.AES.encrypt(reservation.booking_id.toString(), secretKey).toString();
              const nightCount = moment(reservation.check_out_date).diff(moment(reservation.check_in_date), "days");
              const checkInDate = moment(reservation.check_in_date);
              const checkOutDate = moment(reservation.check_out_date);

              return (
                <div className="col" key={reservation.id}>
                  <div className="card h-100 border-0 shadow-sm rounded-3 overflow-hidden transition-card" style={{ border: "2px solid #007bff", background: "#ffffff" }}>
                    <div className="card-header bg-white p-3 d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0 text-dark fw-bold" style={{ fontSize: "1.25rem", lineHeight: "1.2" }}>
                        {reservation.guest_name || "Guest Name"}
                      </h5>
                      <span className="badge bg-primary text-white rounded-pill py-2 px-3">
                        {reservation.booking_status.replace("_", " ").toUpperCase()}
                      </span>
                    </div>
                    <div className="card-body p-3 bg-light">
                      <div className="row g-2">
                        <div className="col-12">
                          <p className="mb-2 text-dark small fw-medium">
                            <strong className="text-primary">Booking No:</strong>{" "}
                            <span className="text-muted">#{reservation.booking_number || "HMS-202502-00026"}</span>
                          </p>
                          <p className="mb-2 text-dark small fw-medium">
                            <PhoneOutlined style={{ fontSize: "18px", color: "#28a745", marginRight: "5px" }} />
                            <strong className="text-primary">Phone:</strong>{" "}
                            <span className="text-muted">{reservation.guest_phone || "1111111111"}</span>
                          </p>
                          <p className="mb-2 text-dark small fw-medium">
                            <strong className="text-primary">Email:</strong>{" "}
                            <span className="text-muted">{reservation.guest_email || "11111@rr.com"}</span>
                          </p>
                          <p className="mb-2 text-dark small fw-medium">
                            <strong className="text-primary">Source:</strong>{" "}
                            <span className="text-muted bg-warning rounded p-1 source-box">
                              {reservation.booking_source || "N/A"}
                            </span>
                          </p>
                        </div>
                        <div className="col-12 d-flex justify-content-center align-items-center">
                          <div className="arrival-timeline d-flex align-items-center gap-2">
                            <span className="text-success check-in-box">
                              {checkInDate.format("DD MMM YYYY")}
                            </span>
                            <Tooltip title={`${nightCount} Night${nightCount !== 1 ? 's' : ''}`}>
                              <span className="night-icon-box d-flex align-items-center justify-content-center">
                                <MoonOutlined style={{ fontSize: "20px", color: "#6c757d" }} />
                              </span>
                            </Tooltip>
                            <span className="text-danger check-out-box">
                              {checkOutDate.format("DD MMM YYYY")}
                            </span>
                          </div>
                        </div>
                        <div className="col-12">
                          <p className="mb-2 text-dark small fw-medium">
                            <strong className="text-primary">Guests:</strong>{" "}
                            <span className="text-muted">
                              <UserOutlined style={{ fontSize: "20px", color: "#007bff", marginRight: "5px" }} /> {reservation.adults || 0}
                              <ChildCareOutlined style={{ fontSize: "16px", color: "#ff7f50", marginRight: "5px", marginLeft: "10px" }} /> {reservation.children || 0}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer bg-white border-top p-3 d-flex justify-content-center align-items-center">
                      <button className="btn btn-primary rounded-pill px-4 py-2">Confirm Booking</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filter Modal (unchanged) */}
        <div className="modal custom-modal fade" id="scrollable-modal" tabIndex={-1} role="dialog" aria-labelledby="filterModalTitle" aria-hidden="true">
          <div className="modal-dialog modal-lg" style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "40%", zIndex: 1060 }} role="document">
            <div className="modal-content h-100">
              <div className="modal-header">
                <h5 className="modal-title" id="filterModalTitle">Filter</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}>
                <div className="row">
                  <div className="col-sm-12 col-md-12 mb-3">
                    <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                      <label className="form-label me-2" style={{ minWidth: "150px" }}>Status:</label>
                      <Select
                        options={statusOptions}
                        placeholder="Select Status"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        isClearable
                        styles={{ control: (base) => ({ ...base, minWidth: "300px", borderRadius: 4, borderColor: "#d9d9d9" }), menu: (base) => ({ ...base, zIndex: 1070 }), menuPortal: (base) => ({ ...base, zIndex: 1070 }) }}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                  <div className="col-sm-12 col-md-12 mb-3">
                    <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                      <label className="form-label me-2" style={{ minWidth: "150px" }}>Booking Source:</label>
                      <Select
                        options={sourceOptions}
                        placeholder="Select Source"
                        value={sourceFilter}
                        onChange={setSourceFilter}
                        isClearable
                        styles={{ control: (base) => ({ ...base, minWidth: "300px", borderRadius: 4, borderColor: "#d9d9d9" }), menu: (base) => ({ ...base, zIndex: 1070 }), menuPortal: (base) => ({ ...base, zIndex: 1070 }) }}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                <button type="button" className="btn btn-light" onClick={handleFilterReset} data-bs-dismiss="modal">Reset</button>
                <button type="button" className="btn btn-primary" data-bs-dismiss="modal">Apply</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for stylish, matching design */}
      <style jsx>{`
        
        .badge {
          font-weight: 600;
          transition: transform 0.3s ease;
        }
        .badge:hover {
          transform: scale(1.05);
        }
        .text-primary {
          color: #007bff !important;
        }
        .text-success {
          color: #28a745 !important;
        }
        .text-danger {
          color: #dc3545 !important;
        }
        .text-muted {
          color: #6c757d !important;
          font-size: 0.875rem;
        }
        .check-in-box {
          background-color: #28a745; /* Green for Check-In */
          padding: 6px 12px;
          border-radius: 4px;
          display: inline-block;
          font-weight: 500;
          color: #ffffff;
        }
        .check-out-box {
          background-color: #dc3545; /* Red for Check-Out */
          padding: 6px 12px;
          border-radius: 4px;
          display: inline-block;
          font-weight: 500;
          color: #ffffff;
        }
        .night-icon-box {
          background-color: #6c757d; /* Gray for Night */
          padding: 6px;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .source-box {
          background-color: #ffc107; /* Yellow for Source */
          padding: 6px 12px;
          border-radius: 4px;
          display: inline-block;
          font-weight: 500;
          color: #ffffff;
        }
        .arrival-timeline {
          font-size: 1rem;
          font-weight: 500;
          color: #333;
          gap: 15px;
        }
        .btn-primary {
          background-color: #007bff;
          border-color: #007bff;
          transition: all 0.3s ease;
        }
        .btn-primary:hover {
          background-color: #0056b3;
          border-color: #0056b3;
          transform: scale(1.05);
        }
        .add-btn {
          border-radius: 8px;
          padding: 8px 16px;
        }
        .bg-gradient-primary {
          background: linear-gradient(45deg, #007bff, #0056b3) !important;
        }
        @media (max-width: 768px) {
          .card {
            margin-bottom: 15px;
          }
          .row-cols-lg-3 > .col {
            flex: 0 0 100%;
            max-width: 100%;
          }
          .arrival-timeline {
            flex-direction: column;
            align-items: center;
            gap: 10px;
          }
          .night-icon-box {
            margin: 10px 0;
          }
        }
      `}</style>
    </div>
  );
};

export default ReservationList;